import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import * as R from 'ramda';

import { GET_WORK_ORDER_HYDRATED } from '@atom/graph/work';
import { useNavigation } from '@atom/hooks/useNavigation';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { schema } from './collinsMpcConstants';
import {
  getAttributeDataRows,
  getAttributeValueById,
  getGeneralDataType,
  getRepairRecommendationsList,
} from './collinsMpcUtilities';
import ElementsSection from './ElementsSection';
import GeneralData from './GeneralData';
import GeneralNotes from './GeneralNotes';
import HoistBeamData from './HoistBeamData';
import InspectionData from './InspectionData';
import PipeRackData from './PipeRackData';
import RepairRecommendations from './RepairRecommendations';

import classes from './collinsMpcPdfReport.module.css';

const CollinsMpcPdfReport = () => {
  const [, { params }] = useNavigation();
  const { workOrderId } = params;
  const { data, loading, error } = useQuery(GET_WORK_ORDER_HYDRATED, {
    variables: {
      id: workOrderId,
      mediaInput: {
        type: 'image',
        page: 1,
        limit: 250,
      },
    },
    fetchPolicy: 'network-only',
  });

  const workOrder = data?.workOrderHydrated;
  const workOrderAsset = workOrder?.assets[workOrder?.inventoryAssetId];
  const unitNumber = getAttributeValueById(
    workOrderAsset?.attributes,
    schema.generalData.unitNumber,
  );

  const generalNotes = R.pathOr(
    null,
    [0, 'value'],
    getAttributeDataRows(workOrderAsset?.attributes, [
      schema.inspectionData.generalNotes,
    ]),
  );

  const repairRecommendationsList = useMemo(() => {
    if (workOrder) {
      return getRepairRecommendationsList(workOrder);
    }

    return [];
  }, [workOrder]);

  const type = getGeneralDataType(workOrderAsset);

  const showGeneralNotes = !isNilOrEmpty(generalNotes);
  const showRepairRecommendations = !isNilOrEmpty(repairRecommendationsList);
  const showPipeRackData = R.includes('Pipe Rack', type);
  const showHoistBeamData = R.includes('Hoist Beam / Trolley Beam', type);
  const showLoaded = !loading && !error;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div>{`Asset: ${workOrder?.inventoryAssetName}`}</div>
        <div>{`Unit No. ${unitNumber}`}</div>
      </div>
      <InspectionData workOrder={workOrder} workOrderAsset={workOrderAsset} />
      <GeneralData workOrderAsset={workOrderAsset} />
      {showGeneralNotes && <GeneralNotes workOrderAsset={workOrderAsset} />}
      {showRepairRecommendations && (
        <RepairRecommendations
          repairRecommendationsList={repairRecommendationsList}
        />
      )}
      {showPipeRackData && <PipeRackData workOrderAsset={workOrderAsset} />}
      {showHoistBeamData && <HoistBeamData workOrderAsset={workOrderAsset} />}
      <ElementsSection workOrder={workOrder} />
      {showLoaded && <div className={classes.loaded} id="loaded" />}
    </div>
  );
};

export default CollinsMpcPdfReport;
